import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV3, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { connectedStoresAdminRouter } from './routing/connectedStoresAdminRouter'
import { connectedStoresRetailerRouter } from './routing/connectedStoresRetailerRouter'
import { connectedStoresPartnerRouter } from './routing/connectedStoresPartnerRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { Permission } from './access-control/permissions'
import {
  caperAccessControl,
  caperTransactionsAccessControl,
  inventoryIntelligenceAccessControl,
  notOnFileAccessControl,
  notOnFileRetailerAccessControl,
  shelfLabelsAccessControl,
  staffBarcodeManagementAccessControl,
  unifiedBarcodesAccessControl,
  uposStatusAccessControl,
} from './access-control/connectedStoresAccess.configuration'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'connected-stores',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'connected-stores-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    admin: [connectedStoresAdminRouter],
    scoped: [connectedStoresRetailerRouter],
    partnerScoped: [connectedStoresPartnerRouter],
  },
  navigationEntries: [
    {
      attachToV3: NavEntryPointV3.Catalog,
      subNavItems: [
        {
          type: 'item',
          labelId: 'connectedStoresDomain.notOnFile.title',
          labelIdV3: 'connectedStoresDomain.notOnFile.title.v3',
          route: 'app-partner-scope-connected-stores-not-on-file',
          llmDescription: 'View and manage not on file items',
          accessControl: notOnFileRetailerAccessControl,
          positionNavV3: 9,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Inventory,
      subNavItems: [
        {
          type: 'item',
          labelId: 'connectedStoresDomain.inventoryIntelligence.title',
          labelIdV3: 'connectedStoresDomain.inventoryIntelligence.nav.liveInventory',
          route: 'app-retailer-scope-connected-stores-inventory-intelligence-live',
          llmDescription: 'View live OOS and low-stock restock priority list',
          accessControl: inventoryIntelligenceAccessControl,
          positionNavV3: 0,
        },
        {
          type: 'item',
          labelId: 'connectedStoresDomain.inventoryIntelligence.title',
          labelIdV3: 'connectedStoresDomain.inventoryIntelligence.nav.trends',
          route: 'app-retailer-scope-connected-stores-inventory-intelligence-trends',
          llmDescription: 'View chronic inventory trends and ordering adjustments',
          accessControl: inventoryIntelligenceAccessControl,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Caper,
      subNavItems: [
        {
          type: 'item',
          labelId: 'connectedStoresAdminDomain.caperTransactions.title',
          labelIdV3: 'connectedStoresAdminDomain.caperTransactions.title.v3',
          route: 'app-retailer-scope-connected-stores-caper-transactions',
          llmDescription: 'View Caper orders and issue receipts',
          accessControl: caperTransactionsAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          labelId: 'connectedStoresDomain.staffBarcodeManagement.title',
          labelIdV3: 'connectedStoresDomain.staffBarcodeManagement.title.v3',
          route: 'app-retailer-scope-connected-stores-staff-barcode-management',
          llmDescription: 'Manage staff barcodes for in-store Caper devices',
          accessControl: staffBarcodeManagementAccessControl,
          inToolPermissions: [
            {
              slug: Permission.CaperStaffBarcodeManagementViewBarcodes,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.CaperStaffBarcodeManagementViewBarcodes,
            },
            {
              slug: Permission.CaperStaffBarcodeManagementEditBarcodes,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.CaperStaffBarcodeManagementEditBarcodes,
            },
            {
              slug: Permission.CaperStaffBarcodeManagementViewBaBarcodes,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.CaperStaffBarcodeManagementViewBaBarcodes,
            },
            {
              slug: Permission.CaperStaffBarcodeManagementEditBaBarcodes,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.CaperStaffBarcodeManagementEditBaBarcodes,
            },
          ],
          positionNavV3: 0,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV3: AdminNavEntryPointV3.Catalog,
      subNavItems: [
        {
          route: 'app-admin-connected-stores-not-on-file',
          subRoutes: ['app-admin-connected-stores-not-on-file-details'],
          labelId: 'connectedStoresAdminDomain.notOnFile.title',
          accessControl: notOnFileAccessControl,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV3: AdminNavEntryPointV3.ConnectedStores,
      subNavItems: [
        {
          route: 'app-admin-connected-stores-caper',
          labelId: 'connectedStoresAdminDomain.caper.title',
          accessControl: caperAccessControl,
          inToolPermissions: [
            {
              slug: Permission.CaperDeviceProvisioningPipelineView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CaperDeviceProvisioningPipelineView,
            },
            {
              slug: Permission.CaperDeviceProvisioningPipelineModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.CaperDeviceProvisioningPipelineModify,
            },
          ],
          subRoutes: [
            'app-admin-connected-stores-caper-session-replay',
            'app-admin-connected-stores-caper-config',
            'app-admin-connected-stores-caper-config-keys',
            'app-admin-connected-stores-caper-config-create',
            'app-admin-connected-stores-caper-config-partner',
            'app-admin-connected-stores-caper-config-retailer',
            'app-admin-connected-stores-caper-config-store',
            'app-admin-connected-stores-caper-config-device',
            'app-admin-connected-stores-caper-features',
            'app-admin-connected-stores-caper-feature-approvals',
            'app-admin-connected-stores-caper-feature-dashboard',
            'app-admin-connected-stores-caper-notification-groups',
            'app-admin-connected-stores-device-provisioning-pipeline',
          ],
        },
        {
          route: 'app-admin-connected-stores-shelf-labels-stores',
          labelId: 'connectedStoresAdminDomain.shelfLabels',
          accessControl: shelfLabelsAccessControl,
          subRoutes: [
            'app-admin-connected-stores-shelf-labels-connections',
            'app-admin-connected-stores-shelf-labels-sync-strategies',
            'app-admin-connected-stores-shelf-labels-automations',
          ],
        },
        {
          route: 'app-admin-connected-stores-upos-status',
          labelId: 'connectedStoresAdminDomain.upos.title',
          accessControl: uposStatusAccessControl,
          subRoutes: [
            'app-admin-connected-stores-upos-retailers',
            'app-admin-connected-stores-upos-profiles',
            'app-admin-connected-stores-upos-stores',
            'app-admin-connected-stores-upos-integrations',
          ],
        },
        {
          route: 'app-admin-connected-stores-unified-barcodes',
          labelId: 'connectedStoresAdminDomain.unifiedBarcodes.title',
          accessControl: unifiedBarcodesAccessControl,
        },
        {
          route: 'app-admin-connected-stores-caper-cloud-access',
          labelId: 'connectedStoresAdminDomain.caperCloudAccess.title',
          accessControl: caperAccessControl,
        },
      ],
    },
  ],
  // NOTE TO DEV: run `pnpm ipp domain register <your-system-name>` to register your domain with Totem here
}

export default definition
